import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import PropTypes from "prop-types";
import { useSnackbar } from "notistack";
import DsButton from "@accredo/fe-shared/theme/components/Button.jsx";

// Dialog to add a new insurer contact. It has its own form state but
// deliberately renders no <form>: React bubbles submit events through portals
// into an enclosing form. The create button triggers handleSubmit via onClick
// instead. A 422 from `onCreate` shows its email error inline and keeps the
// dialog open.
const newContactSchema = yup.object({
  name: yup.string().nullable(),
  email: yup
    .string()
    .email("Ungültige E-Mail-Adresse")
    .required("E-Mail-Adresse ist erforderlich"),
});

const NewInsurerContactDialog = ({
  open,
  onClose,
  onCreate,
  defaultName = "",
  defaultEmail = "",
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { isSubmitting },
  } = useForm({
    defaultValues: { name: defaultName, email: defaultEmail },
    resolver: yupResolver(newContactSchema),
    mode: "onChange",
  });

  useEffect(() => {
    if (open) {
      reset({ name: defaultName, email: defaultEmail });
    }
  }, [open, defaultName, defaultEmail, reset]);

  const handleClose = () => {
    reset({ name: defaultName, email: defaultEmail });
    onClose();
  };

  const handleCreate = handleSubmit(async (values) => {
    try {
      await onCreate({ name: values.name.trim(), email: values.email.trim() });
      handleClose();
    } catch (error) {
      const email_errors = error?.response?.data?.errors?.email;
      if (error?.response?.status === 422 && email_errors) {
        setError("email", { message: email_errors[0] });
      } else {
        enqueueSnackbar("Kontakt konnte nicht angelegt werden", {
          variant: "error",
        });
      }
    }
  });

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Neuer Kontakt beim Versicherer</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Name"
                helperText="Optional. Ohne Namen wird der Versicherer angezeigt."
                fullWidth
              />
            )}
          />
          <Controller
            name="email"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="E-Mail-Adresse"
                type="email"
                required
                fullWidth
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: "16px" }}>
        <DsButton variant="tertiary" onClick={handleClose}>
          Abbrechen
        </DsButton>
        <DsButton
          variant="primary"
          onClick={handleCreate}
          disabled={isSubmitting}
        >
          Kontakt anlegen
        </DsButton>
      </DialogActions>
    </Dialog>
  );
};

NewInsurerContactDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  defaultName: PropTypes.string,
  defaultEmail: PropTypes.string,
};

export default NewInsurerContactDialog;
